import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Clock, Trash2 } from "lucide-react";
import { formatTimestamp } from "../lib/time";
import { T } from "../lib/i18n";

const TEMPLATES = [
  { text: "Cake cutting in 3 minutes — come gather around!", kind: "now" },
  { text: "Photo queue is now open — head to the lounge.", kind: "info" },
  { text: "Dessert stations are live — enjoy!", kind: "info" },
  { text: "The Who Will game is starting — open the Games tab!", kind: "celebrate" },
  { text: "Last call at the bar in 15 minutes.", kind: "warning" },
  { text: "First dance is about to begin.", kind: "celebrate" },
];

export default function MomentsComposer() {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("info");
  const [scheduled, setScheduled] = useState("");
  const [moments, setMoments] = useState([]);

  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/moments"); setMoments(data); } catch {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const send = async () => {
    if (!text.trim()) return toast.error("Add a message");
    try {
      const payload = { text, kind, projector: true };
      if (scheduled) {
        const iso = new Date(scheduled).toISOString();
        payload.scheduled_for = iso;
      }
      await api.post("/moments", payload);
      toast.success(scheduled ? "Scheduled" : "Announced to all guests — in-app + push");
      setText(""); setScheduled("");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/moments/${id}`); refresh(); } catch (e) { toast.error(formatErr(e)); }
  };

  const upcoming = moments.filter((m) => !m.sent_at);
  const recent = moments.filter((m) => m.sent_at).slice(0, 6);

  return (
    <div className="card-elev p-5">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-xl">{T("Announce")} · {T("Moments")}</div>
        <span className="text-[10px] tracking-luxe text-motif-muted ml-2">{T("in-app + push notification to all guests")}</span>
      </div>

      <textarea
        className="input-motif"
        rows={2}
        placeholder="Type a moment or pick a template…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="moment-text"
      />

      <div className="flex flex-wrap gap-1.5 mt-2">
        {TEMPLATES.map((tpl, i) => (
          <button
            key={tpl.text}
            onClick={() => { setText(tpl.text); setKind(tpl.kind); }}
            className="text-[11px] px-2.5 py-1 rounded-full border border-motif text-motif-muted hover:bg-accent-motif"
            data-testid={`moment-tpl-${i}`}
          >
            {tpl.text.slice(0, 30)}…
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select className="input-motif text-sm py-2 flex-1 min-w-[110px]" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="moment-kind">
          <option value="info">{T("Info")}</option>
          <option value="now">Now</option>
          <option value="celebrate">{T("Celebrate")}</option>
          <option value="warning">{T("Warning")}</option>
        </select>
        <input
          type="datetime-local"
          className="input-motif text-sm py-2 flex-1 min-w-[170px]"
          value={scheduled}
          onChange={(e) => setScheduled(e.target.value)}
          data-testid="moment-schedule"
        />
        <button onClick={send} className="btn-primary text-xs px-3 py-2 flex items-center justify-center gap-1.5 flex-shrink-0" data-testid="moment-send">
          {scheduled ? <Clock className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
          {scheduled ? "Schedule" : "Push now"}
        </button>
      </div>

      {upcoming.length > 0 && (
        <div className="mt-4 pt-3 border-t border-motif">
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("scheduled")}</div>
          <div className="space-y-1.5">
            {upcoming.map((m) => (
              <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg bg-accent-motif" data-testid={`moment-upcoming-${m.id}`}>
                <Clock className="w-3 h-3 text-primary-motif" />
                <div className="flex-1 text-xs">
                  <span className="font-medium">{formatTimestamp(m.scheduled_for)}</span>
                  <span className="ml-2 text-motif-muted">{m.text}</span>
                </div>
                <button onClick={() => del(m.id)} className="btn-ghost text-xs p-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4 pt-3 border-t border-motif">
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("recent")}</div>
          <div className="space-y-1">
            {recent.map((m) => (
              <div key={m.id} className="text-xs text-motif-muted">
                {formatTimestamp(m.sent_at)} — {m.text}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
